import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Markdown } from '@/lib/markdownRenderer';
import { TableOfContents } from '@/components/ui/TableOfContents';
import { FloatingFeedbackButton } from '@/components/ui/FloatingFeedbackButton';
import { LanguageFallbackBanner } from '@/components/ui/LanguageFallbackBanner';
import { BookOpen, Package, Database, Upload, Users, BarChart3 } from 'lucide-react';
import { getGuidesForLanguage, isLanguageAvailable } from '@/generated/guides';
import { useDocumentationTranslations } from '@/hooks/useDocumentationTranslations';

// Map guide slugs to their corresponding tabs
const guideMapping: Record<string, string> = {
  'getting-started': 'getting-started',
  'product-management': 'product-management',
  'data-structure': 'data-structure',
  'import-export': 'import-export',
  'team-permissions': 'team-permissions',
  'analytics-reports': 'analytics-reports',
  // Legacy mappings
  '01-getting-started': 'getting-started',
  '02-product-management': 'product-management',
  '03-data-structure': 'data-structure',
  '04-import-export': 'import-export',
  '06-team-permissions': 'team-permissions',
  '07-analytics-reports': 'analytics-reports',
};

export default function DocumentationPage() {
  const [activeTab, setActiveTab] = useState<string>('');
  const [pendingAnchor, setPendingAnchor] = useState<string>('');
  const [searchParams] = useSearchParams();
  const { getHeaderLabels, getTabLabels, getStateLabels, currentLanguage } =
    useDocumentationTranslations();

  // Get guides for the current language with fallback to English
  const guides = getGuidesForLanguage(currentLanguage);
  const isContentInUserLanguage = isLanguageAvailable(currentLanguage);
  const fallbackLanguage = 'en';

  // Define the documentation tabs with translated labels
  const documentationTabs = useMemo(
    () => [
      {
        id: 'getting-started',
        label: getTabLabels.gettingStarted.label,
        icon: BookOpen,
        description: getTabLabels.gettingStarted.description,
      },
      {
        id: 'product-management',
        label: getTabLabels.productManagement.label,
        icon: Package,
        description: getTabLabels.productManagement.description,
      },
      {
        id: 'data-structure',
        label: getTabLabels.dataStructure.label,
        icon: Database,
        description: getTabLabels.dataStructure.description,
      },
      {
        id: 'import-export',
        label: getTabLabels.importExport.label,
        icon: Upload,
        description: getTabLabels.importExport.description,
      },
      {
        id: 'team-permissions',
        label: getTabLabels.teamPermissions.label,
        icon: Users,
        description: getTabLabels.teamPermissions.description,
      },
      {
        id: 'analytics-reports',
        label: getTabLabels.analyticsReports.label,
        icon: BarChart3,
        description: getTabLabels.analyticsReports.description,
      },
    ],
    [getTabLabels]
  );

  // Find guides for each tab
  const getGuidesForTab = (tabId: string) => {
    return guides.filter(guide => guideMapping[guide.slug] === tabId);
  };

  // Get first tab that has content
  const firstTabWithContent =
    documentationTabs.find(tab => getGuidesForTab(tab.id).length > 0)?.id || 'getting-started';

  // Handle URL parameters for tab and anchor navigation
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    const anchor = window.location.hash.replace('#', '');

    // Always respect the tab parameter from the URL if it's valid
    if (tabParam && documentationTabs.some(tab => tab.id === tabParam)) {
      // Only set if it's different to avoid unnecessary re-renders
      if (activeTab !== tabParam) {
        setActiveTab(tabParam);
      }
      // Store anchor to apply after content loads
      if (anchor) {
        setPendingAnchor(anchor);
      }
    } else if (!activeTab) {
      setActiveTab(firstTabWithContent);
      // If there's an anchor but no tab specified, use the first tab and still try to scroll
      if (anchor) {
        setPendingAnchor(anchor);
      }
    }
  }, [searchParams, firstTabWithContent, documentationTabs, activeTab]);

  // Handle direct anchor navigation (when page loads with hash)
  useEffect(() => {
    const handleHashChange = () => {
      const anchor = window.location.hash.replace('#', '');
      if (anchor && activeTab) {
        setPendingAnchor(anchor);
      }
    };

    // Check for initial hash
    handleHashChange();

    // Listen for hash changes
    window.addEventListener('hashchange', handleHashChange);

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, [activeTab]);

  // Handle anchor scrolling after content is loaded and tab is set
  useEffect(() => {
    if (pendingAnchor && activeTab) {
      const scrollToAnchor = () => {
        // Look for the exact anchor first
        let element = document.getElementById(pendingAnchor);

        // If not found, try alternative anchor formats that remarkSlug might generate
        if (!element) {
          // Try lowercase version
          const lowerAnchor = pendingAnchor.toLowerCase();
          element = document.getElementById(lowerAnchor);
        }

        if (!element) {
          // Try GitHub-style slug format (what remarkSlug generates)
          const slugAnchor = pendingAnchor
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/(^-|-$)/g, '');
          element = document.getElementById(slugAnchor);
        }

        if (!element) {
          // Try to find by heading text content
          const headings = document.querySelectorAll('h1, h2, h3, h4, h5, h6');
          for (const heading of headings) {
            const headingText = heading.textContent?.toLowerCase();
            const anchorText = pendingAnchor.toLowerCase().replace(/-/g, ' ');
            if (headingText?.includes(anchorText) || anchorText.includes(headingText || '')) {
              element = heading as HTMLElement;
              break;
            }
          }
        }

        if (element) {
          // Clear the pending anchor since we found it
          setPendingAnchor('');

          // Use multiple scroll methods for better reliability
          const performScroll = () => {
            // Method 1: scrollIntoView
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start',
              inline: 'nearest',
            });

            // Method 2: Direct scroll calculation (fallback)
            setTimeout(() => {
              const rect = element.getBoundingClientRect();
              const headerOffset = 100; // Account for sticky header
              const scrollTop = window.pageYOffset + rect.top - headerOffset;
              window.scrollTo({ top: scrollTop, behavior: 'smooth' });
            }, 100);
          };

          performScroll();

          // Focus for accessibility
          if (element.tabIndex === -1) {
            element.tabIndex = -1;
          }
          element.focus({ preventScroll: true });

          return true;
        }

        return false;
      };

      // Try to scroll immediately
      if (scrollToAnchor()) {
        return;
      }

      // If immediate scroll failed, retry with increasing delays
      const timeouts = [100, 300, 600, 1000, 2000, 3000];
      const timeoutIds: NodeJS.Timeout[] = [];

      timeouts.forEach(delay => {
        const timeoutId = setTimeout(() => {
          if (pendingAnchor) {
            // Only try if anchor is still pending
            scrollToAnchor();
          }
        }, delay);
        timeoutIds.push(timeoutId);
      });

      // Cleanup timeouts if component unmounts or anchor changes
      return () => {
        timeoutIds.forEach(id => clearTimeout(id));
      };
    }
  }, [pendingAnchor, activeTab]);

  // Set initial active tab if not set
  useEffect(() => {
    if (!activeTab) {
      setActiveTab(firstTabWithContent);
    }
  }, [firstTabWithContent, activeTab]);

  // Scroll to top when tab changes (using ScrollToTop utility logic)
  useEffect(() => {
    // Only scroll if activeTab is set and this is not the initial load
    if (activeTab && firstTabWithContent) {
      // Use the same scroll logic as ScrollToTop component for consistency
      const scrollContainer = document.querySelector('.documentation-container');

      // Immediate scroll
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;

      // Scroll the documentation container specifically
      if (scrollContainer) {
        scrollContainer.scrollTop = 0;
      }

      // Use requestAnimationFrame to ensure it happens after render
      requestAnimationFrame(() => {
        window.scrollTo(0, 0);
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
        if (scrollContainer) {
          scrollContainer.scrollTop = 0;
        }
      });

      // Also add a small timeout as a fallback for complex pages
      setTimeout(() => {
        window.scrollTo(0, 0);
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
        if (scrollContainer) {
          scrollContainer.scrollTop = 0;
        }
      }, 100);
    }
  }, [activeTab, firstTabWithContent]); // Only trigger when activeTab changes

  return (
    <div
      className="documentation-container documentation-page-fix h-full overflow-y-auto bg-background"
      style={{ height: 'calc(100vh - 60px)' }} // Account for header height
    >
      {/* STICKY LAYOUT - Direct children of scrolling container */}
      <div className="relative flex min-h-full">
        {/* Left Sticky Sidebar - Direct child */}
        <div
          style={{
            position: 'sticky',
            top: '0px',
            backgroundColor: 'hsl(var(--background))',
            zIndex: 10,
            width: '16rem',
            maxHeight: 'calc(100vh - 60px)',
            overflowY: 'auto',
            padding: '1rem',
            borderRight: '1px solid hsl(var(--border))',
          }}
          className="hidden shrink-0 xl:block"
        >
          <h3 className="mb-4 text-sm font-semibold text-muted-foreground">
            {getHeaderLabels.platformGuides}
          </h3>
          <div className="mt-4 space-y-2">
            {documentationTabs.map(tab => {
              const IconComponent = tab.icon;
              const hasContent = getGuidesForTab(tab.id).length > 0;

              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  disabled={!hasContent}
                  className={`flex w-full items-start rounded-md p-2 text-left transition-colors ${
                    activeTab === tab.id ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'
                  }`}
                >
                  <IconComponent className="mr-2 mt-0.5 size-4 shrink-0" />
                  <span className="text-sm">{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Main Content Area */}
        <div className="min-w-0 flex-1 p-8" data-tour-target="documentation">
          {/* Header */}
          <div className="mb-8 space-y-4">
            <div>
              <h1 className="text-4xl font-bold tracking-tight">{getHeaderLabels.title}</h1>
              <p className="mt-2 text-xl text-muted-foreground">{getHeaderLabels.description}</p>
            </div>
          </div>

          {/* Language Fallback Banner */}
          {!isContentInUserLanguage && currentLanguage !== fallbackLanguage && (
            <LanguageFallbackBanner
              requestedLanguage={currentLanguage}
              fallbackLanguage={fallbackLanguage}
              className="mb-6"
            />
          )}

          {/* Active Tab Content */}
          {activeTab && (
            <div className="space-y-6">
              <div>
                <h2 className="mb-4 text-3xl font-bold tracking-tight">
                  {documentationTabs.find(tab => tab.id === activeTab)?.label}
                </h2>
                <div className="h-px bg-border"></div>
              </div>

              {(() => {
                const tabGuides = getGuidesForTab(activeTab);
                const IconComponent =
                  documentationTabs.find(tab => tab.id === activeTab)?.icon || BookOpen;

                return tabGuides.length > 0 ? (
                  <div
                    className={`docs-content docs-content-${activeTab} prose prose-gray max-w-none dark:prose-invert`}
                  >
                    {tabGuides.map((guide, index) => (
                      <div key={guide.slug}>
                        {index > 0 && <div className="my-8 border-t border-border"></div>}
                        <Markdown content={guide.content} />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className={`docs-content docs-content-${activeTab}`}>
                    <div className="py-12 text-center">
                      <IconComponent className="mx-auto mb-4 size-16 text-muted-foreground" />
                      <h3 className="mb-2 text-lg font-semibold">
                        {documentationTabs.find(tab => tab.id === activeTab)?.label}
                      </h3>
                      <p className="mb-6 text-muted-foreground">
                        {documentationTabs.find(tab => tab.id === activeTab)?.description}
                      </p>
                      <div className="mx-auto max-w-md rounded-lg bg-muted p-6">
                        <p className="text-sm text-muted-foreground">{getStateLabels.comingSoon}</p>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}
        </div>

        {/* Right Sticky Sidebar - Direct child */}
        <div
          style={{
            position: 'sticky',
            top: '0px',
            backgroundColor: 'hsl(var(--background))',
            zIndex: 10,
            width: '16rem',
            maxHeight: 'calc(100vh - 60px)',
            overflowY: 'auto',
            padding: '1rem',
            borderLeft: '1px solid hsl(var(--border))',
          }}
          className="hidden shrink-0 xl:block"
        >
          <h3 className="mb-4 text-sm font-semibold text-muted-foreground">
            {getHeaderLabels.onThisPage}
          </h3>
          {activeTab && (
            <TableOfContents key={activeTab} containerSelector={`.docs-content-${activeTab}`} />
          )}
        </div>
      </div>

      {/* Floating Feedback Button */}
      <FloatingFeedbackButton />
    </div>
  );
}
